import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import uuid from 'uuid';
import { ComponentFactory } from './ComponentRegistry';
import { getColourFromString } from '../../utils/ColourNameGenerator';
import {
  createFormRedirectLink,
  createLoopLink,
  createSplitConvergeLink,
  EDGE_TYPES,
} from '../../utils/CanvasConstants';
import { send_request } from '../../utils/Request';

const useProjectStore = create(
  subscribeWithSelector((set, get) => ({
    // --- Core project state ---
    savedComponent: undefined,
    refreshCanvas: undefined,
    errorSaving: undefined,
    project_id: '',
    project_name: '',
    publishedVersion: undefined,
    draftVersion: undefined,
    last_modified: undefined,
    components: [],
    imagePath: undefined,
    useBpmnJoinBehavior: false,
    error: false,
    errors: [],
    project_description: '',
    pageStatus: '',
    projectStatus: undefined,
    UIOnlyIconPos: undefined,
    canvas: undefined,
    nodes: undefined,
    edges: undefined,

    // --- this.state object flattened ---
    zoom_level: 85,
    active_diagram: {},
    timezone: 'UTC',
    locked: true,
    tier: 'FREE',
    orgName: '',
    userName: '',
    userDisplayName: '',
    diagramEngine: {},
    workflowCategoriesMapping: {},
    workflowCategories: [],
    allowCanvasTranslation: true,
    production: false,
    canvasView: false,
    dbIds: [],
    clientId: null,
    isV2Billing: undefined,
    billingInitialized: false,

    // --- Component editing state ---
    saveTriggerExecuteTest: false,
    saveTriggerSessionId: null,
    componentName: '',
    componentDescription: '',
    // Inline editing: which node's field is focused for placeholder insertion
    activeComponentId: null,
    activeInputRef: null,
    metadataEnabled: false,
    startTimestampLabel: '',
    endTimestampLabel: '',
    metadataTimestampFormat: 'yyyy-MM-dd HH:mm:ss',
    selectedPlaceholder: { selected: '' },
    targetDatabaseId: '',
    componentTrayOpen: false,

    // --- Component editing actions ---
    setComponentName: name => set({ componentName: name }),
    setComponentDescription: desc => set({ componentDescription: desc }),
    setMetadataSettings: settings => set(settings),
    setSelectedPlaceholder: p => set({ selectedPlaceholder: p }),
    setTargetDatabaseId: id => set({ targetDatabaseId: id }),
    setComponentTrayOpen: open => set({ componentTrayOpen: open }),
    setActiveComponentId: id => set({ activeComponentId: id }),
    setActiveInputRef: ref => set({ activeInputRef: ref }),
    clearComponentState: () =>
      set({
        saveTriggerExecuteTest: false,
        saveTriggerSessionId: null,
        selectedPlaceholder: { selected: '' },
        targetDatabaseId: '',
        componentTrayOpen: false,
        metadataEnabled: false,
        startTimestampLabel: '',
        endTimestampLabel: '',
        metadataTimestampFormat: 'yyyy-MM-dd HH:mm:ss',
      }),

    openComponentTray: () => set({ componentTrayOpen: true }),
    resetComponentTrayTrigger: () => set({ componentTrayOpen: false }),

    // --- State object helpers (legacy compat) ---
    // Expose a `state` getter-like object for consumers that read projectStore.state.X
    // These consumers will be migrated to read flat props directly.

    setWorkflow: workflow => {
      const update = {
        project_id: workflow.projectId,
        project_name: workflow.name,
        last_modified: workflow.lastModified,
        project_description: workflow.description,
        imagePath: workflow.imagePath,
        projectStatus: workflow.status,
        useBpmnJoinBehavior: workflow.useBpmnJoinBehavior || false,
        errors: [],
        error: false,
      };
      if (workflow.UIOnlyIconPos) {
        update.UIOnlyIconPos = workflow.UIOnlyIconPos;
      }
      if (workflow.components) {
        update.components = workflow.components;
      } else {
        update.components = [];
      }
      set(update);
      if (!workflow.components) {
        get().setCanvas([]);
      }
    },

    setCanvas: canvas => set({ canvas }),
    getCanvas: () => get().canvas,
    getNodes: () => get().nodes,
    setNodes: nodes => set({ nodes }),
    getEdges: () => get().edges,
    setEdges: edges => set({ edges }),

    createNode: (component, template, isAiGenerated) => {
      let isFromConditionalPath = false;
      let color = getColourFromString();

      if (
        component.componentData !== undefined &&
        component.componentData !== null &&
        component.componentData.isFromConditionalPath
      ) {
        isFromConditionalPath = component.componentData.isFromConditionalPath;
      }
      if (
        component.componentData !== undefined &&
        component.componentData !== null &&
        component.componentData.color
      ) {
        color = component.componentData.color;
      }

      return {
        id: component.componentId,
        type: 'component',
        data: {
          error: component.error,
          logo: template.logo,
          hasInput: template.hasInput ? template.hasInput : false,
          hasOutput: template.hasOutput ? template.hasOutput : false,
          type: component.type,
          displayName: template.displayName,
          next: component.nextComponents,
          label: component.rawComponentData?.name
            || component.componentData?.name
            || component.generatedTitle
            || template.displayName,
          baseColor: template.baseColor,
          description: component.description,
          instructions: component.instructions,
          isTemp: isAiGenerated,
          color: color,
          fromWorkflowPath: isFromConditionalPath,
          metadataEnabled: component.metadataEnabled || false,
          startTimestampLabel: component.startTimestampLabel || '',
          endTimestampLabel: component.endTimestampLabel || '',
          metadataTimestampFormat: component.metadataTimestampFormat || 'yyyy-MM-dd HH:mm:ss',
          schemaVersion: component.rawComponentData?.schemaVersion ?? component.componentData?.schemaVersion ?? null,
        },
        position: { x: component.viewData.x, y: component.viewData.y },
      };
    },

    createDBNode: (dbId, template, component, compData) => {
      let viewDataDetails = { x: 100, y: 100 };
      if (component.viewData != null) {
        viewDataDetails = {
          x: component.viewData.x + 570,
          y: component.viewData.y + 20,
        };
      } else if (compData != null) {
        viewDataDetails = {
          x: compData.position.x + 570,
          y: compData.position.y + 20,
        };
      }
      const uiOnlyIconPos = get().UIOnlyIconPos;
      if (uiOnlyIconPos != null) {
        let dbDetail = uiOnlyIconPos.find(x => x.nodeId === dbId);
        viewDataDetails = dbDetail ? dbDetail['viewData'] : viewDataDetails;
      }
      return {
        id: dbId,
        type: 'dbNode',
        data: {
          logo: template.logo,
          hasDb: true,
          displayName: template.displayName,
          label: template.displayName,
          baseColor: template.baseColor,
          type: 'UI.dbIcon',
          newTabUrl: template.newTabUrl,
        },
        position: {
          x: viewDataDetails.x,
          y: viewDataDetails.y,
        },
      };
    },

    createLink: (
      type,
      fromWorkflowPath,
      newNodeId,
      nextId,
      baseColor,
      isLoop = false,
      maxIterations = null,
      exitCondition = null,
      edgeCondition = null
    ) => {
      if (edgeCondition != null) {
        return {
          id: uuid(),
          source: newNodeId,
          target: nextId,
          markerEnd: { type: 'arrowclosed', color: edgeCondition.isDefault ? '#999999' : '#F5A623' },
          type: EDGE_TYPES.GATEWAYLINK,
          label: 'gatewayLink',
          data: {
            showDeleteButton: false,
            edgeCondition: edgeCondition,
          },
          style: {
            strokeWidth: '3px',
            stroke: edgeCondition.isDefault ? '#999999' : '#F5A623',
            ...(edgeCondition.isDefault ? { strokeDasharray: '5,5' } : {}),
          },
        };
      }

      if (isLoop) {
        return {
          id: uuid(),
          source: newNodeId,
          target: nextId,
          markerEnd: { type: 'arrowclosed', color: '#9c27b0' },
          type: EDGE_TYPES.LOOPLINK,
          label: 'loopLink',
          data: {
            showDeleteButton: false,
            isLoop: true,
            maxIterations: maxIterations,
            exitCondition: exitCondition,
          },
          style: {
            strokeWidth: '3px',
            stroke: '#9c27b0',
            strokeDasharray: '5,5',
          },
        };
      }

      return {
        id: uuid(),
        source: newNodeId,
        target: nextId,
        markerEnd: {
          type: type === 'conditional_workflow_path' ? '' : 'arrowclosed',
          color: '#868686',
        },
        type: 'link',
        label: 'componentLink',
        data: { showDeleteButton: type === 'conditional_workflow_path' },
        style: {
          strokeWidth: '3px',
          stroke: fromWorkflowPath ? '#868686' : baseColor,
        },
      };
    },

    createDBLink: (newNodeId, nextId, baseColor, label) => {
      return {
        id: uuid(),
        source: newNodeId,
        target: nextId,
        markerEnd: { type: 'arrowclosed', color: '#868686' },
        type: EDGE_TYPES.FLOATINGLINK,
        data: { hasDb: true },
        label: label,
        style: {
          strokeWidth: 2,
          stroke: '#868686',
          strokeDasharray: '6 3',
        },
      };
    },

    readNotes: notes => {
      let noteIds = notes
        .map(item => {
          if (item.data) return item.id;
        })
        .filter(a => a != undefined);
      let result = [];
      if (notes && notes.length != 0) {
        notes.map((note, id) => {
          if (note.dragHandle) delete note.dragHandle;
          if (note.type == 'note') {
            delete note.style;
            delete note.source;
            delete note.sourceHandle;
            delete note.target;
            delete note.targetHandle;
            delete note.arrowHeadType;
          } else if (note.type == 'link') {
            delete note.position;
            note.data = { label: note.data ? note.data.label : '' };
          }
        });
        result = notes.filter(item => {
          if (item.type === 'note') {
            return item;
          } else if (noteIds.indexOf(item.source) !== -1 && noteIds.indexOf(item.target) !== -1) {
            return item;
          }
        });
      }
      return result;
    },

    createDbNodeLogic: (component, dbIds, diagram, dbId, compData) => {
      if (!new Set(dbIds.values()).has(dbId)) {
        const dbNode = get().createDBNode(dbId, ComponentFactory['UI.dbIcon'], component, compData);
        diagram.push(dbNode);
      }
      let newLink = '';
      if (
        component.type === 'query_database_record' ||
        component.type === 'trigger_on_database_update'
      ) {
        newLink = get().createDBLink(dbId, component.componentId, '#1C1B1F', 'link');
      } else {
        newLink = get().createDBLink(component.componentId, dbId, '#1C1B1F', 'link');
      }
      dbIds.set(component.componentId, dbId);
      diagram.push(newLink);
    },

    readDiagram: (components, isAiGenerated) => {
      let diagram = [];
      let dbIds = new Map([]);
      let compNextMap = new Map([]);
      const { createNode, createLink, createDbNodeLogic } = get();

      if (components) {
        components.map((component, id) => {
          const raw = component.rawComponentData;
          const cd = component.componentData;
          const isV2 = raw?.schemaVersion >= 2;
          const data = isV2 ? raw : cd?.data?.[0];
          const startingLoopComponentId = data?.targetComponentId;
          const redirectsToAnotherForm = isV2 ? raw?.linkedComponent : data?.linkedComponent;
          const inFactory = ComponentFactory[component.type] !== undefined;
          if (inFactory) {
            try {
            const newNode = createNode(component, ComponentFactory[component.type], isAiGenerated);
            if (startingLoopComponentId) {
              diagram.push(createLoopLink(component.componentId, startingLoopComponentId));
            }
            if (redirectsToAnotherForm) {
              const componentIdsToRedirect = isV2 ? raw?.linkedComponents : data?.linkedComponents;
              if (componentIdsToRedirect) {
                componentIdsToRedirect.forEach(id => {
                  diagram.push(createFormRedirectLink(component.componentId, id));
                });
              }
            }

            const dbId = isV2
              ? (raw?.targetDatabaseId ?? cd?.targetDatabaseId)
              : (cd?.targetDatabaseId
                ?? (Array.isArray(cd?.data)
                  ? cd?.data?.[0]?.targetDatabaseId
                  : cd?.data?.targetDatabaseId));
            if (dbId != null) {
              createDbNodeLogic(component, dbIds, diagram, dbId);
              newNode.data.hasDb = true;
            }

            diagram.push(newNode);

            if (newNode.data.next && newNode.data.next.length != 0) {
              newNode.data.next.map((next, nextId) => {
                const newLink = createLink(
                  newNode.data.type,
                  newNode.data.fromWorkflowPath,
                  newNode.id,
                  next.componentId,
                  newNode.data.baseColor,
                  next.isLoop || false,
                  next.maxIterations || null,
                  next.exitCondition || null,
                  next.edgeCondition || null
                );

                if (newNode.data.type === 'conditional_workflow_path') {
                  compNextMap.set(next.componentId, newNode.id);
                }
                if (newNode.id && next.componentId) diagram.push(newLink);
              });
            }
            } catch (err) {
              console.error('[readDiagram] ERROR creating node for', component.componentId, component.type, err);
            }
          }
        });
      }
      compNextMap.forEach((value, key) => {
        let conditional_workflow = diagram.find(obj => obj.id === key);
        if (conditional_workflow) {
          conditional_workflow.data.parentId = value;
        }
      });

      // Create split-converge links for loop_through_list -> converge_threads pairs
      const loopThroughListNodes = diagram.filter(n => n.data?.type === 'loop_through_list');
      const convergeThreadsNodes = diagram.filter(n => n.data?.type === 'converge_threads');

      if (loopThroughListNodes.length > 0 && convergeThreadsNodes.length > 0) {
        for (const loopNode of loopThroughListNodes) {
          const visited = new Set();
          const queue = [loopNode.id];

          while (queue.length > 0) {
            const currentId = queue.shift();
            if (visited.has(currentId)) continue;
            visited.add(currentId);

            const convergeNode = convergeThreadsNodes.find(n => n.id === currentId);
            if (convergeNode) {
              diagram.push(createSplitConvergeLink(loopNode.id, convergeNode.id));
              break;
            }

            const outgoingLinks = diagram.filter(e => e.type === 'link' && e.source === currentId);
            for (const link of outgoingLinks) {
              if (!visited.has(link.target)) {
                queue.push(link.target);
              }
            }
          }
        }
      }

      set({ dbIds });
      return diagram;
    },

    findLinks: (canvas, sourceId) => {
      let next = canvas.filter((link, id) => {
        if (link.type == 'link' && link.sourceHandle == null) {
          return link.source == sourceId;
        }
      });
      return next.map((nextItem, id) => {
        return { componentId: nextItem.target, nodeId: nextItem.target };
      });
    },

    removeDuplicateLinks: nextComponents => {
      return nextComponents.reduce((unique, o) => {
        if (!unique.some(obj => obj.componentId === o.componentId && obj.nodeId === o.nodeId)) {
          unique.push(o);
        }
        return unique;
      }, []);
    },

    removeUnknownComponents: (nodes, nextComponents) => {
      let nodeIds = nodes.map(node => {
        if (node.type == 'component' && node.id != undefined) return node.id;
      });
      let result = nextComponents;
      for (var i = 0; i < nextComponents.length; i++) {
        if (nodeIds.indexOf(nextComponents[i].componentId) == -1) {
          result = result.filter(item => {
            return nextComponents[i].componentId != item.componentId;
          });
        }
      }
      nextComponents = result;
      return nextComponents;
    },

    // Live-sync components from canvas nodes & edges so downstream consumers
    // (placeholders, merge workflow, etc.) react immediately to changes.
    syncFromCanvas: (nodes, edges) => {
      const components = get().components;
      if (!components || components.length === 0) return;

      // Build set of component node IDs still on canvas
      const canvasNodeIds = new Set();
      const canvasNodeLabels = {};
      if (nodes) {
        for (const node of nodes) {
          if (node.type === 'component') {
            canvasNodeIds.add(node.id);
            if (node.data?.label) {
              canvasNodeLabels[node.id] = node.data.label;
            }
          }
        }
      }

      // Build adjacency: sourceId -> [{ componentId, nodeId }]
      const adjacency = {};
      if (edges) {
        for (const edge of edges) {
          // Only regular component links (same filter as findLinks)
          if (edge.type === 'link' && edge.sourceHandle == null) {
            const sourceId = edge.source;
            if (!adjacency[sourceId]) adjacency[sourceId] = [];
            // Deduplicate
            if (!adjacency[sourceId].some(n => n.componentId === edge.target)) {
              adjacency[sourceId].push({ componentId: edge.target, nodeId: edge.target });
            }
          }
        }
      }

      let changed = false;

      // Remove components that no longer exist on canvas
      let updated = components;
      if (canvasNodeIds.size > 0) {
        updated = components.filter(comp => {
          if (!canvasNodeIds.has(comp.componentId)) {
            changed = true;
            return false;
          }
          return true;
        });
      }

      // Update nextComponents and component names
      updated = updated.map(comp => {
        let compChanged = false;
        let result = comp;

        // Sync nextComponents from edges
        const newNext = adjacency[comp.componentId] || [];
        const oldNext = comp.nextComponents || [];
        if (
          oldNext.length !== newNext.length ||
          !oldNext.every((o, i) => o.componentId === newNext[i]?.componentId)
        ) {
          compChanged = true;
          result = { ...result, nextComponents: newNext };
        }

        // Sync component name from canvas node label
        const canvasLabel = canvasNodeLabels[comp.componentId];
        if (canvasLabel && comp.componentData?.name !== canvasLabel) {
          compChanged = true;
          result = {
            ...result,
            componentData: { ...result.componentData, name: canvasLabel },
          };
        }

        if (compChanged) changed = true;
        return compChanged ? result : comp;
      });

      if (changed) {
        set({ components: updated });
      }
    },

    readCanvasIntoComponents: canvas => {
      let result = [];
      canvas.map(canvasItem => {
        if (canvasItem.type == 'component' || canvasItem.type == 'dbNode') {
          let positionRecord = {
            componentId: canvasItem.id,
            type: canvasItem.data.type,
            viewData: canvasItem.position,
            componentData: { name: canvasItem.data.label },
          };
          result.push(positionRecord);
        }
      });
      return result;
    },

    readCanvasIntoNotes: nodes => {
      let items = [];
      for (let node of nodes) {
        if (node.type !== 'note') continue;
        items.push({
          id: node.id,
          type: node.type,
          data: node.data,
          position: node.position,
        });
      }
      return items;
    },

    saveCanvas: canvas => {
      const { findLinks, removeDuplicateLinks, removeUnknownComponents } = get();
      const aggregate = get().getAggregate();
      let components = aggregate.components;
      let result = [];
      let notes = [];

      if (components != null) {
        canvas.map(diagramItem => {
          if (diagramItem.type == 'component') {
            let nextComponents = findLinks(canvas, diagramItem.id);
            nextComponents = removeDuplicateLinks(nextComponents);
            nextComponents = removeUnknownComponents(canvas, nextComponents);
            let update = components.find(component => {
              return diagramItem.id == component.componentId;
            });

            if (update) {
              let updateIndex = components.findIndex(obj => {
                return obj.componentId == update.componentId;
              });
              if (updateIndex >= 0) {
                update = get().updateComponent(update, diagramItem, nextComponents);
                result.push(update);
              }
            } else {
              let newComponent = get().createComponent(diagramItem, nextComponents);
              result.push(newComponent);
            }
          }
        });
      } else if (components == null) {
        components = [];
        canvas.map(diagramItem => {
          if (diagramItem.type == 'component') {
            let nextComponents = findLinks(canvas, diagramItem.id);
            nextComponents = removeDuplicateLinks(nextComponents);
            let newComponent = get().createComponent(diagramItem, nextComponents);
            result.push(newComponent);
          }
        });
      }

      notes = canvas.filter(item => {
        return item.type == 'note';
      });
      let noteIds = notes.map(item => {
        return item.id;
      });
      let noteLinks = canvas
        .map(item => {
          if (item.type == 'link' && noteIds.indexOf(item.source) != -1) return item;
        })
        .filter(a => a != undefined);
      notes.concat(noteLinks);

      result.map(component => {
        delete component.componentData;
      });
      notes = [...notes, ...noteLinks];

      return { components: result, notes: notes };
    },

    updateComponent: (toUpdate, item, nextComponents) => {
      toUpdate.type = item.data.type;
      toUpdate.viewData.x = item.position.x;
      toUpdate.viewData.y = item.position.y;
      toUpdate.nextComponents = nextComponents;
      return toUpdate;
    },

    createComponent: (item, nextComponents) => {
      return {
        componentId: item.id,
        type: item.data.type,
        viewData: item.position,
        nextComponents: nextComponents,
      };
    },

    setProjectName: projectName => set({ project_name: projectName }),

    saveProjectName: async () => {
      let body = get().getAggregate();
      body.projectName = body.project_name;
      send_request(
        `project-service/project/update/meta/${body.project_id}`,
        body,
        '',
        'PATCH'
      ).catch(e => {
        console.log(e);
      });
    },

    setLatestVersion: (draftVersion, publishedVersion) =>
      set({ draftVersion, publishedVersion }),

    clear: () => {
      const currentComponents = get().components;
      set({
        project_id: '',
        project_name: '',
        project_description: '',
        pageStatus: '',
        error: false,
        errors: [],
        useBpmnJoinBehavior: false,
        components: currentComponents !== undefined ? [] : [],
      });
    },

    // Computed replacements (selector helpers)
    getIsEmpty: () => get().project_id === '',

    getAggregate: () => {
      const s = get();
      return {
        project_id: s.project_id,
        project_name: s.project_name,
        draftVersion: s.draftVersion,
        publishedVersion: s.publishedVersion,
        project_description: s.project_description,
        imagePath: s.imagePath,
        components: s.components ? [...s.components] : [],
      };
    },

    getCurrentComponents: node_id => {
      return get().getAggregate().components;
    },

    handleDocsRedirect: component_type => {
      switch (component_type) {
        case 'conditional_logic':
          component_type = 'cond_logic';
          break;
        case 'conditional_workflow':
          component_type = 'cond_workflow';
          break;
        case 'document_editor':
          component_type = 'doc_editor';
          break;
        case 'knowledge_bank':
          component_type = 'know_bank';
          break;
        case 'calculation':
          component_type = 'calculator';
          break;
        case 'code':
          component_type = 'run_code';
          break;
        case 'api_request_and_response':
          component_type = '';
          break;
        case 'send_slack_message':
          component_type = 'slack';
          break;
        case 'upload_spreadsheet':
          component_type = 'bulk_assessment';
          break;
        case 'query_database_record':
          component_type = 'query_database';
          break;
        case 'create_database_record':
          component_type = 'create_record';
          break;
        case 'update_database_record':
          component_type = 'update_record';
          break;
        case 'trigger_from_zapier':
          component_type = 'trigger_workflow_zap';
          break;
        case 'trigger_a_zap':
          component_type = 'trigger_zap';
          break;
        case 'merge_workflow':
          component_type = 'merge_workflow';
          break;
        case 'loop_through_list':
          component_type = 'loop_list';
          break;
        case 'converge_threads':
          component_type = 'converge_threads';
          break;
        case 'trigger_from_a_workflow':
          component_type = 'trigger_workflow';
          break;
        case 'trigger_another_workflow':
          component_type = 'trigger_workflow';
          break;
        case 'form':
          component_type = 'form_builder';
          break;
        case 'sequential_form':
          component_type = 'seq_form';
          break;
        case 'form_section':
          component_type = 'form_section';
          break;
        case 'session_variables':
          component_type = 'session_variables';
          break;
        case 'trigger_via_email':
          component_type = 'trigger_via_email';
          break;
        case 'ai_assistant':
          component_type = 'ai_assistant';
          break;
        case 'conditional_workflow_path':
          component_type = 'conditional_workflow_path';
          break;
      }
      let url = 'https://docs.workflow86.com/docs/components/' + component_type;
      window.open(url);
    },
  }))
);

export default useProjectStore;
